const BASE_URL = process.env.REACT_APP_APPS_SCRIPT_URL || process.env.REACT_APP_SCRIPT_URL;

// Simple in-memory cache for static reference data
const cache = {};
async function cached(key, fn) {
  if (cache[key]) return cache[key];
  cache[key] = await fn();
  return cache[key];
}

// Debug: log URL on load
if (!BASE_URL) console.error('BASE_URL is undefined! Check env vars.');

async function apiFetch(action, payload = {}) {
  if (!BASE_URL || BASE_URL === 'YOUR_APPS_SCRIPT_URL_HERE') {
    throw new Error('Apps Script URL not configured.');
  }

  const payloadStr = JSON.stringify(payload);
  const isLargePayload = payloadStr.length > 5000;

  let params;
  if (isLargePayload) {
    params = new URLSearchParams({ action });
  } else {
    params = new URLSearchParams({
      action,
      ...Object.fromEntries(
        Object.entries(payload).map(([k, v]) => [k, typeof v === 'object' ? JSON.stringify(v) : v])
      )
    });
  }

  const url = BASE_URL + '?' + params.toString();
  const body = JSON.stringify({ action, ...payload });

  let res = await fetch(url, { method: 'POST', redirect: 'follow', body });
  if (!res.ok) {
    await new Promise(r => setTimeout(r, 2000));
    res = await fetch(url, { method: 'POST', redirect: 'follow', body });
  }
  if (!res.ok) throw new Error(`API error ${res.status}`);
  const data = await res.json();
  if (data.error) throw new Error(data.error);
  return data;
}

async function call(action, payload = {}) {
  return apiFetch(action, payload);
}

async function post(action, payload = {}) {
  return apiFetch(action, payload);
}

export const marketsApi = {
  getAll: () => cached('markets', () => call('getMarkets')),
  create: (market) => post('createMarket', { market }),
  update: (market) => post('updateMarket', { market }),
  toggleActive: (id, isActive) => post('toggleMarket', { id, isActive }),
};

export const productsApi = {
  getAll: () => cached('products', () => call('getProducts')),
  create: (product) => post('createProduct', { product }),
  update: (product) => post('updateProduct', { product }),
  toggleActive: (id, isActive) => post('toggleProduct', { id, isActive }),
};

export const marketProductsApi = {
  getForMarket: (marketId) => call('getMarketProducts', { marketId }),
  toggle: (marketId, productId, isActive) =>
    post('toggleMarketProduct', { marketId, productId, isActive }),
};

export const usersApi = {
  getAll: () => cached('users', () => call('getUsers')),
  create: (user) => post('createUser', { user }),
  update: (user) => post('updateUser', { user }),
  toggleActive: (id, isActive) => post('toggleUser', { id, isActive }),
};

export const marketDaysApi = {
  getAll: () => call('getMarketDays'),
  get: (id) => call('getMarketDay', { id }),
  create: (marketDay) => post('createMarketDay', { marketDay }),
  update: (marketDay) => post('updateMarketDay', { marketDay }),
  scanSheet: (payload) => post('scanSheet', payload),
};

export const salesRecordsApi = {
  getForMarketDay: (marketDayId) => call('getSalesRecords', { marketDayId }),
  update: (record) => post('updateSalesRecord', { record }),
};

export const packListsApi = {
  getAll: () => call('getPackLists'),
  get: (id) => call('getPackList', { id }),
  create: (packList) => post('createPackList', { packList }),
  update: (packList) => post('updatePackList', { packList }),
  getItems: (packListId) => call('getPackListItems', { packListId }),
  updateItem: (item) => post('updatePackListItem', { item }),
  scanActuals: (payload) => post('scanActuals', payload),
  pushPackedToMarketDay: (packListId, marketDayId) => post('pushPackedToMarketDay', { packListId, marketDayId }),
};

export const packingAssignmentsApi = {
  getAll: () => call('getPackingAssignments'),
  set: (saturdayMarketId, sundayMarketId) => post('setPackingAssignment', { saturdayMarketId, sundayMarketId }),
};
export const kegsApi = {
  getAll: () => call('getKegs'),
  create: (keg) => post('createKeg', { keg }),
  update: (keg) => post('updateKeg', { keg }),
};
export const kegRecordsApi = {
  getForMarketDay: (marketDayId) => call('getKegRecords', { marketDayId }),
  create: (record) => post('createKegRecord', { record }),
  update: (record) => post('updateKegRecord', { record }),
};
export const settingsApi = {
  getAll: () => call('getSettings'),
  save: (settings) => post('saveSettings', { settings }),
};
export const intercompanyApi = {
  calculate: (params) => post('calculateBilling', params),
  save: (billing) => post('saveBilling', { billing }),
  getAll: () => call('getBillingRecords'),
};
